import React from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardHeader,
  CardBody,
  Typography,
  Icon,
  ButtonIcon,
  Button,
  Tooltip,
} from '@statsbomb/kitbag-components';
import StatGrid from '../../../components/Tables/StatGrid/StatGrid';
import LoaderLabelled from '../../../components/LoaderLabelled/LoaderLabelled';
import TeamIcon from './TeamIcon';
import PlayDescriptionCell from './PlayDescriptionCell';
import { formatPlayClock } from './PlayDescription';
import { formatBool } from './PlaysGrid.dataManipulation';
import {
  getScoringPlays,
  formatTryResult,
} from './ScoringPlays.dataManipulation';
import {
  formatYardlineWithTeam,
  getYardsWon,
} from '../../../utils/helpers/play';
import { convertIntToNth } from '../../../utils/helpers/formatting';
import { API_PLAY_TYPE_KEYS } from '../../../utils/constants/api';
import {
  ScoringPlaysWrap,
  ScoringPlaysEmptyState as EmptyState,
  EmptyState as ErrorState,
  OffenseCell as OffenseCellWrap,
  VideoIconWrap,
} from './GameOverview.styles';

const downAndDist = (play) => {
  if (play.down == null || play.yardsToGo == null) return '';
  const down = convertIntToNth(play.down) || String(play.down);
  const yards = Math.floor(play.yardsToGo);
  return `${down} and ${yards}`;
};

const formatEpa = (epa) => {
  if (epa == null) return '';
  return epa > 0 ? `+${epa.toFixed(2)}` : epa.toFixed(2);
};

const orDash = (value) => (value == null || value === '' ? '-' : value);

const gainedText = (play) =>
  play.type === API_PLAY_TYPE_KEYS.FIELD_GOAL
    ? '-'
    : orDash(getYardsWon(play.yardsNet));

// Offense cell: same team icon and short name as the Play by Play table's
// Offense column.
function OffenseCell({ data, context }) {
  const team = data.isHome ? context.game.homeTeam : context.game.awayTeam;
  return (
    <OffenseCellWrap>
      <TeamIcon team={team} context={context} />
      {team.shortName || team.name}
    </OffenseCellWrap>
  );
}

OffenseCell.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  context: PropTypes.object.isRequired,
};

// Description: the shared play description (action note plus penalty text).
// The column is flex, so no auto-size hook-up.
function DescriptionCell({ data }) {
  return <PlayDescriptionCell play={data.raw} />;
}

DescriptionCell.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.object.isRequired,
};

// Same definition as the data dictionary's explosive field.
const EXPLOSIVE_TOOLTIP = 'Explosive play';

// "Exp?" is cryptic on its own, so the header explains it on hover.
function ExplosiveHeader({ displayName }) {
  return (
    <Tooltip content={EXPLOSIVE_TOOLTIP} placement="top-start">
      <span>{displayName}</span>
    </Tooltip>
  );
}

ExplosiveHeader.propTypes = {
  displayName: PropTypes.string.isRequired,
};

function VideoCell({ data, context }) {
  return (
    <VideoIconWrap>
      <Tooltip content="Watch play" placement="top-start">
        <ButtonIcon
          icon="Play"
          variant="ghost"
          size="small"
          title="Watch play"
          ariaLabel="Watch play"
          onClick={() => context.onSelectPlay(data.raw)}
        />
      </Tooltip>
    </VideoIconWrap>
  );
}

VideoCell.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  context: PropTypes.object.isRequired,
};

// 11 columns, ordered like the Play by Play table's play rows (clock, down and
// distance, yardline, then the outcome columns, Description, then Score). Text
// columns use valueGetters; the team / explosive / video / async-description
// columns use cell renderers. Every column hugs its content (see
// autoSizeToContent) except Description, which takes the spare width, and the
// video action column, which is fixed at 48px and comes first.
const COLUMN_DEFS = [
  {
    colId: 'video',
    headerName: '',
    cellRenderer: VideoCell,
    headerClass: 'action-cell',
    cellClass: 'action-cell',
    width: 48,
    minWidth: 48,
    maxWidth: 48,
    suppressAutoSize: true,
  },
  {
    colId: 'offense',
    headerName: 'Offense',
    cellRenderer: OffenseCell,
  },
  {
    colId: 'clock',
    headerName: 'Clock',
    valueGetter: ({ data }) => orDash(formatPlayClock(data)),
  },
  {
    colId: 'downDist',
    headerName: 'Down & Dist',
    valueGetter: ({ data }) => orDash(downAndDist(data)),
  },
  {
    colId: 'yardline',
    headerName: 'Yardline',
    valueGetter: ({ data, context }) => {
      const { homeTeam, awayTeam } = context.game;
      return orDash(
        formatYardlineWithTeam(
          data.yardLine,
          data.isHome ? homeTeam : awayTeam,
          data.isHome ? awayTeam : homeTeam,
        ),
      );
    },
  },
  {
    colId: 'gained',
    headerName: 'Yards gained',
    type: 'rightAligned',
    valueGetter: ({ data }) => gainedText(data),
  },
  {
    colId: 'result',
    headerName: 'Result',
    // Room for "Touchdown - XP no good" (about 22 characters plus cell
    // padding); autoSize still grows it past this when a value is longer.
    minWidth: 200,
    valueGetter: ({ data }) =>
      orDash(
        [data.driveResult, formatTryResult(data.extraPoint)]
          .filter(Boolean)
          .join(' - '),
      ),
  },
  {
    colId: 'explosive',
    headerName: 'Exp?',
    headerComponent: ExplosiveHeader,
    valueGetter: ({ data }) => formatBool(data.explosive),
  },
  {
    colId: 'epa',
    headerName: 'EPA',
    type: 'rightAligned',
    valueGetter: ({ data }) => orDash(formatEpa(data.scrimmageEpa)),
  },
  {
    colId: 'description',
    headerName: 'Description',
    cellRenderer: DescriptionCell,
    flex: 1,
    minWidth: 160,
  },
  {
    colId: 'score',
    headerName: 'Score',
    // Selected team's score first, matching the page header.
    valueGetter: ({ data, context }) =>
      context.isHomeTeam
        ? `${data.homeScore} - ${data.awayScore}`
        : `${data.awayScore} - ${data.homeScore}`,
  },
];

// Size every column except the flex one (Description) and the fixed video
// column to its content, as the Play by Play table does. autoSize would set
// an explicit width on a flex column and freeze it.
const autoSizeToContent = ({ api }) => {
  const ids = api
    .getColumns()
    .filter((c) => !c.getColDef().flex && !c.getColDef().suppressAutoSize)
    .map((c) => c.getColId());
  api.autoSizeColumns(ids, false);
};

function ScoringPlays({
  plays,
  game,
  loading,
  error,
  onRetry,
  onSelectPlay,
  isHomeTeam,
  selectedTeamColor,
  opponentTeamColor,
}) {
  // Loading is only ever driven by the `loading` prop. A finished load that
  // resolved no game (no plays with a drive) is an empty result, not a loader.
  const gameResolved = !!game?.homeTeam && !!game?.awayTeam;
  const showLoader = loading;
  const showError = !loading && !!error && !gameResolved;
  const scoringPlays =
    showLoader || !gameResolved ? [] : getScoringPlays(plays, game);

  let body;
  if (showLoader) {
    body = <LoaderLabelled text="Loading plays" height="24rem" />;
  } else if (showError) {
    body = (
      <ErrorState data-testid="scoring-plays-error" role="alert">
        <Icon variant="Info" size="regular" />
        <Typography variant="headingLarge">Unable to Load Plays</Typography>
        <Typography variant="bodyRegular">
          We couldn&apos;t load the scoring plays for this game. Check your
          connection and try again.
        </Typography>
        <Button variant="primary" size="small" onClick={() => onRetry()}>
          Try Again
        </Button>
      </ErrorState>
    );
  } else if (scoringPlays.length === 0) {
    body = <EmptyState>No scoring plays recorded for this game.</EmptyState>;
  } else {
    body = (
      <StatGrid
        columnDefs={COLUMN_DEFS}
        rowData={scoringPlays}
        // Render every column up front so autoSizeToContent measures them all.
        suppressColumnVirtualisation
        onFirstDataRendered={autoSizeToContent}
        context={{
          game,
          onSelectPlay,
          isHomeTeam,
          selectedTeamId: isHomeTeam ? game?.homeTeam?.id : game?.awayTeam?.id,
          selectedTeamColor,
          opponentTeamColor,
        }}
      />
    );
  }

  return (
    <ScoringPlaysWrap>
      <Card>
        <CardHeader>
          <Typography variant="headingMedium">Scoring Plays</Typography>
        </CardHeader>
        <CardBody>{body}</CardBody>
      </Card>
    </ScoringPlaysWrap>
  );
}

ScoringPlays.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  plays: PropTypes.arrayOf(PropTypes.object),
  game: PropTypes.shape({
    homeTeam: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      shortName: PropTypes.string,
      colorPrimary: PropTypes.string,
      colorSecondary: PropTypes.string,
    }),
    awayTeam: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      shortName: PropTypes.string,
      colorPrimary: PropTypes.string,
      colorSecondary: PropTypes.string,
    }),
  }),
  loading: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  error: PropTypes.object,
  onRetry: PropTypes.func,
  onSelectPlay: PropTypes.func,
  isHomeTeam: PropTypes.bool,
  selectedTeamColor: PropTypes.string,
  opponentTeamColor: PropTypes.string,
};

ScoringPlays.defaultProps = {
  plays: [],
  game: null,
  loading: false,
  error: null,
  onRetry: () => {},
  onSelectPlay: () => {},
  isHomeTeam: true,
  selectedTeamColor: null,
  opponentTeamColor: null,
};

export default ScoringPlays;
